import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Star, Heart } from "lucide-react";

const STORES = {
  android: { url: "https://play.google.com/store/apps/details?id=it.feriepermessi.app", label: "Google Play" },
  ios: { url: "https://apps.apple.com/it/app/id6762294169?action=write-review", label: "App Store" },
  windows: { url: "https://apps.microsoft.com/detail/9NN4TXP00FPD?hl=it-IT&gl=IT", deepLink: "ms-windows-store://review/?ProductId=9NN4TXP00FPD", label: "Microsoft Store" },
};

export const getStoreLink = () => {
  const p = window.Capacitor?.getPlatform?.();
  if (p === "android" || p === "ios") return STORES[p];
  const ua = (navigator.userAgent || "").toLowerCase();
  if (/android/.test(ua)) return STORES.android;
  if (/iphone|ipad|ipod|macintosh/.test(ua)) return STORES.ios;
  return STORES.windows;
};

const SESSION_KEY = "review_prompt_shown";

export const ReviewPromptDialog = () => {
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("ask"); // ask | confirm | thanks

  useEffect(() => {
    if (!token || !user || sessionStorage.getItem(SESSION_KEY)) return;
    let timer;
    axios
      .get(`${API}/review-prompt/eligibility`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!res.data.show) return;
        timer = setTimeout(() => {
          sessionStorage.setItem(SESSION_KEY, "1");
          setOpen(true);
        }, 5000);
      })
      .catch(() => {});
    return () => clearTimeout(timer);
  }, [token, user]);

  const store = getStoreLink();

  const send = (action) =>
    axios.post(`${API}/review-prompt/action`, { action, store: store.label }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});

  const handleLater = () => { send("later"); setOpen(false); };
  const openStore = () => {
    if (!store.deepLink) return window.open(store.url, "_blank");
    // Windows: apre l'app Microsoft Store sulla pagina "Valuta e recensisci"; se non si apre, ripiega sul sito
    const t = Date.now();
    window.location.href = store.deepLink;
    setTimeout(() => { if (document.hasFocus() && Date.now() - t < 2500) window.open(store.url, "_blank"); }, 1800);
  };
  const handleLink = () => { openStore(); send("clicked"); setStep("confirm"); };
  const handleNotYet = () => { send("not_yet"); setOpen(false); };
  const handleReviewed = () => {
    send("reviewed");
    setStep("thanks");
    setTimeout(() => setOpen(false), 2000);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-lg [&>button:last-child]:hidden" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()} data-testid="review-prompt-dialog">
        {step === "thanks" ? (
          <div className="py-8 text-center space-y-3" data-testid="review-prompt-thanks">
            <Heart className="h-12 w-12 text-rose-500 mx-auto fill-rose-500" />
            <p className="text-xl font-bold text-slate-900">Grazie di cuore!</p>
            <p className="text-slate-600">Il tuo sostegno ci aiuta a crescere.</p>
          </div>
        ) : step === "confirm" ? (
          <div className="space-y-5 text-center" data-testid="review-prompt-confirm">
            <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
              <Star className="h-6 w-6 text-amber-500" />
            </div>
            <p className="text-lg font-bold text-slate-900">Hai lasciato la recensione su {store.label}?</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={handleReviewed} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="review-prompt-reviewed-btn">Sì, fatto!</Button>
              <Button variant="outline" onClick={handleNotYet} data-testid="review-prompt-not-yet-btn">Non ancora</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-5 text-center">
            <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
              <Star className="h-6 w-6 text-amber-500" />
            </div>
            <div className="space-y-2">
              <p className="text-xl font-bold text-slate-900">Come ti trovi con Ferie e Permessi?</p>
              <p className="font-medium text-slate-800">La tua opinione per noi è importante!</p>
              <p className="text-sm text-slate-600">
                Se l'app ti è utile, lasciaci una recensione sul tuo store: bastano pochi secondi e ci aiuterai a migliorare e a far conoscere l'app.
              </p>
              <p className="text-sm text-slate-700">Grazie per il tuo supporto! ❤️</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={handleLink} className="bg-amber-500 hover:bg-amber-600 text-white whitespace-normal h-auto py-2.5" data-testid="review-prompt-cta">
                <Star className="h-4 w-4 mr-2 shrink-0" />
                Lascia una recensione su {store.label}
              </Button>
              <Button variant="outline" onClick={handleLater} data-testid="review-prompt-later-btn">Più tardi</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
